import { LitElement, unsafeCSS, html, nothing } from "lit";
import { state, customElement, property } from "lit/decorators.js";
import { Mark } from "prosemirror-model";
import { type LinkPreview } from "@/composables/link-preview";
import style from "./style.scss?inline";

import "@shoelace-style/shoelace/dist/components/card/card.js";
import "@shoelace-style/shoelace/dist/components/button-group/button-group.js";
import "@shoelace-style/shoelace/dist/components/copy-button/copy-button.js";
import "@shoelace-style/shoelace/dist/components/tooltip/tooltip.js";

@customElement("c-link-preview-popup")
export class LinkPreviewPopup extends LitElement {
    @state()
    linkPreview: LinkPreview | undefined;

    @state()
    href: string;

    @state()
    private domain: string;

    @property({ attribute: false })
    anchorNode?: { marks?: Mark[]; attrs?: { ref?: string } };

    static styles = unsafeCSS(style);

    private extractDomain(url: string): string {
        try {
            const urlObject = new URL(url);
            return urlObject.hostname.replace(/^www\./, "");
        } catch {
            // Return the original string if URL is invalid
            return url;
        }
    }

    constructor({
        href,
        getLinkPreview,
    }: {
        href: string;
        getLinkPreview: Promise<LinkPreview | undefined>;
    }) {
        super();
        this.href = href;
        this.domain = this.extractDomain(href);

        getLinkPreview.then((linkPreview) => {
            this.linkPreview = linkPreview;
        });
    }

    private renderIntegrationStatus() {
        if (!this.linkPreview?.integration_status) {
            return nothing;
        }

        const status = this.linkPreview.integration_status;
        let statusMessage = "";
        let statusIcon = "";

        switch (status) {
            case "connected":
                return nothing;
            case "missing_integration":
                statusMessage = "Integration not configured";
                statusIcon = "⚠️";
                break;
            case "missing_credentials":
                statusMessage = "Credentials missing";
                statusIcon = "⚠️";
                break;
            case "error":
                statusMessage = "Integration error";
                statusIcon = "❌";
                break;
            default:
                return nothing;
        }

        return html`
            <div class="integration-status">
                <sl-tooltip content=${statusMessage}>
                    <span class="status-icon">${statusIcon}</span>
                </sl-tooltip>
                <span class="status-message">${statusMessage}</span>
            </div>
        `;
    }

    private renderPreviewHeader() {
        return html`<div slot="header" class="preview-header">
            <a href="${this.href}" title="${this.href}" target="_blank">
                ${this.linkPreview?.title
                    ? html`
                          <sl-tooltip content=${this.linkPreview.title}>
                              <h3>${this.linkPreview.title}</h3>
                              <small class="href-domain">
                                  ${this.linkPreview.domain}
                              </small>
                          </sl-tooltip>
                      `
                    : html`
                          <small class="href-domain"> ${this.domain} </small>
                      `}
            </a>
            <sl-button-group>
                <sl-copy-button
                    copy-label="Copy link"
                    value=${this.href}
                ></sl-copy-button>
            </sl-button-group>
        </div>`;
    }

    private renderPreviewDescription() {
        if (!this.linkPreview?.description) return nothing;

        // Note: all whitespace is shown in this div, hence
        // the code formatting for the section. So no whitespace will be
        // shown before or after the description.
        // prettier-ignore
        return html`
            <div class="preview-description">${
                this.linkPreview.description
            }</div>
        `;
    }

    private renderPreviewImage() {
        if (!this.linkPreview?.image) return nothing;
        return html`<img
            slot="image"
            class="preview-image"
            src="${this.linkPreview.image}"
        />`;
    }

    render() {
        return html`
            <sl-card class="link-preview-card">
                ${this.renderPreviewImage()} ${this.renderPreviewHeader()}
                ${this.renderPreviewDescription()}
                ${this.linkPreview
                    ? nothing
                    : html`
                          <p class="body">
                              <sl-skeleton effect="sheen"></sl-skeleton>
                          </p>
                      `}
                ${this.renderIntegrationStatus()}
            </sl-card>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "c-link-preview-popup": LinkPreviewPopup;
    }
}
